import type React from 'react';
import { storageObjectPathFromUrl } from './storage-url';
import type { TierLicenceKind } from './tier-licences';
import { Info, Image as ImageIcon, List } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * Shared constants and helpers for the product create + edit pages. The two
 * pages used to maintain identical copies of these — extracted here so a
 * change to (e.g.) the refund-policy list lands on both flows simultaneously.
 *
 * Anything draft- or page-specific stays in the page file. Only values that
 * are byte-for-byte equal across both pages live here.
 */

export type ViewMode = 'form' | 'page';

export type SubscriptionFrequency = 'weekly' | 'fortnightly' | 'monthly' | '3-months' | 'annually';

export const subscriptionFrequencies: ReadonlyArray<{ value: SubscriptionFrequency; label: string }> = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: '3-months', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

/**
 * How a buyer pays for a single tier. `one-off` is a single charge that grants
 * permanent access; `subscription` bills the tier price every
 * `subscriptionFrequency` until the buyer cancels, exactly like a
 * `pricingModel: 'subscription'` product but scoped to this tier. A tiered
 * product can mix both (e.g. a one-off "Files" tier and a recurring "Updates"
 * tier). Tiers saved before per-tier subscriptions shipped carry no
 * `purchaseType` at all — read them through `tierPurchaseType` below, which
 * treats anything unrecognised as one-off.
 */
export type TierPurchaseType = 'one-off' | 'subscription';

export type PriceTier = {
  id: number;
  name: string;
  price: string;
  description: string;
  purchaseType: TierPurchaseType;
  /** Billing cadence. Only meaningful when `purchaseType` is 'subscription'. */
  subscriptionFrequency?: SubscriptionFrequency;
  /** A school licence and, for a team, its size (src/lib/tier-licences.ts). */
  licence?: TierLicenceKind;
  licenceSeats?: number;
};

/**
 * Most tiers a `pricingModel: 'tiered'` product may offer. A buyer picks exactly
 * one tier, so a long list is a decision pushed onto them rather than a richer
 * offer. Both product forms cap the list here, and both save paths re-check it,
 * because form state can be restored from a localStorage draft the seller (or
 * anything else on the origin) may have edited.
 */
export const MAX_PRICE_TIERS = 6;

/**
 * Ids of the tiers that share a name with another tier, compared trimmed and
 * case-insensitively. Two tiers called "Pro" are indistinguishable on the
 * product page, in the cart and on the receipt, so publishing is blocked until
 * they differ. Blank names are skipped: that is the empty-field problem, not the
 * duplicate one. Ids come back stringified so the number ids on the doc and the
 * string keys in form state look up the same way.
 */
export function duplicateTierNameIds(
  tiers: ReadonlyArray<{ id: number | string; name?: string }> | null | undefined,
): Set<string> {
  const firstIdForName = new Map<string, string>();
  const dupes = new Set<string>();
  for (const tier of tiers ?? []) {
    const key = (tier?.name || '').trim().toLowerCase();
    if (!key) continue;
    const id = String(tier.id);
    const first = firstIdForName.get(key);
    if (first !== undefined) {
      dupes.add(first);
      dupes.add(id);
    } else {
      firstIdForName.set(key, id);
    }
  }
  return dupes;
}

/**
 * Seed name for a freshly added tier: the first "New Tier N" no existing tier is
 * using. A fixed "New Tier" would land the seller in the duplicate-name error
 * the moment they added two without renaming the first.
 */
export function nextTierName(tiers: ReadonlyArray<{ name?: string }> | null | undefined): string {
  const list = tiers ?? [];
  const taken = new Set(list.map((t) => (t?.name || '').trim().toLowerCase()));
  for (let n = list.length + 1; ; n++) {
    const candidate = `New Tier ${n}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

/** Purchase type of a tier, defaulting legacy/malformed tiers to one-off. */
export function tierPurchaseType(tier: { purchaseType?: string } | null | undefined): TierPurchaseType {
  return tier?.purchaseType === 'subscription' ? 'subscription' : 'one-off';
}

/** True when this tier bills recurringly rather than once. */
export function isSubscriptionTier(tier: { purchaseType?: string } | null | undefined): boolean {
  return tierPurchaseType(tier) === 'subscription';
}

/**
 * Billing cadence of a subscription tier. Falls back to monthly so a tier that
 * lost (or never had) an explicit frequency still renders and prices sanely —
 * the server applies the same default when it stages the subscription plan.
 */
export function tierFrequency(
  tier: { subscriptionFrequency?: string } | null | undefined,
): SubscriptionFrequency {
  const value = tier?.subscriptionFrequency;
  return subscriptionFrequencies.some((f) => f.value === value) ?
    (value as SubscriptionFrequency) :
    'monthly';
}

export const refundPolicies: ReadonlyArray<{ value: string; label: string }> = [
  { value: '7d', label: '7-day money back' },
  { value: '14d', label: '14-day money back' },
  { value: '30d', label: '30-day money back' },
  { value: '60d', label: '60-day money back' },
  { value: '90d', label: '90-day money back' },
  { value: 'custom', label: 'Custom Policy' },
];

// A custom policy is free text, so it needs a NUMBER attached or the listing can
// promise "60-day guarantee" while the server enforces the platform default.
// Mirrors CUSTOM_REFUND_WINDOW_MIN/MAX_DAYS and customRefundWindowDays in
// functions/src/index.ts — keep in sync; the server is the one that enforces it.
export const CUSTOM_REFUND_WINDOW_MIN_DAYS = 1;
export const CUSTOM_REFUND_WINDOW_MAX_DAYS = 365;
export const CUSTOM_REFUND_WINDOW_DEFAULT_DAYS = 30;

/** Clamp a seller's custom window to what the server will actually enforce. */
export function clampCustomRefundDays(v: unknown): number {
  const n = Math.floor(Number(v));
  if (!Number.isFinite(n) || n <= 0) return CUSTOM_REFUND_WINDOW_DEFAULT_DAYS;
  return Math.min(CUSTOM_REFUND_WINDOW_MAX_DAYS, Math.max(CUSTOM_REFUND_WINDOW_MIN_DAYS, n));
}

/**
 * The window a policy actually enforces, in days, or null when the seller offers
 * no self-service refunds. Buyers can still escalate to support in that case, so
 * a null here is "no seller-side path", never "no refund possible".
 */
export function refundWindowDaysFor(
  policy: { enabled?: boolean; type?: string; customDays?: unknown } | null | undefined,
): number | null {
  if (!policy?.enabled || policy.type === 'none') return null;
  switch (policy.type) {
    case '7d': return 7;
    case '14d': return 14;
    case '30d': return 30;
    case '60d': return 60;
    case '90d': return 90;
    case 'custom': return clampCustomRefundDays(policy.customDays);
    default: return CUSTOM_REFUND_WINDOW_DEFAULT_DAYS;
  }
}

export const totalSteps = 3;

export const stepDetails: ReadonlyArray<{ title: string; icon: LucideIcon }> = [
  { title: 'Product Information', icon: Info },
  { title: 'Media & Files', icon: ImageIcon },
  { title: 'Product Details', icon: List },
];

export const DRAFT_KEY_CREATE = 'draftProduct:create';
/** A new bundle keeps its own unsaved draft, so starting one never offers back a half-made product, or the other way round. */
export const DRAFT_KEY_CREATE_BUNDLE = 'draftProduct:bundle';
export const DRAFT_KEY_EDIT_PREFIX = 'draftProduct:edit:';

/**
 * The toast a first launch ends on, in both forms, after which the form goes to
 * the products list. It replaced the "your product is live" dialog (owner's
 * call, 2026-10-08). The bell notice with the same words is written by the
 * server on the same draft-to-live edge (functions/src/lib/product-launch-notice.ts),
 * and its link opens the product's payment link.
 */
export function launchToast(productName: string): { title: string; description: string } {
  const name = productName.trim();
  return {
    title: 'Congratulations, your product is live!',
    description: name ? `You can share "${name}" via its payment link.` : 'You can share it via its payment link.',
  };
}

// ---------------------------------------------------------------------------
// Rich product descriptions
//
// A description may now carry images, tables, rules and a video embed. Three
// consequences fall out of that, and all three are handled here so the create
// and edit pages cannot drift apart on any of them.
// ---------------------------------------------------------------------------

/**
 * The soft limit shown under the description editor, in VISIBLE characters.
 *
 * Still advisory, and deliberately so: it is a writing guide, and a seller who
 * runs a little over it is not doing anything harmful. What changed is that
 * "advisory" no longer means "unbounded". The raw HTML is now hard-capped by
 * DESCRIPTION_HTML_MAX_LENGTH in src/lib/sanitize-html.ts (and its binding
 * mirror in functions), which sits far above anything this counter can
 * legitimately reach.
 *
 * The two numbers measure different things on purpose. This one counts prose,
 * so a seller is never pushed into cutting sentences to pay for markup. That
 * one counts bytes, because bytes are what every marketplace visitor downloads.
 */
export const DESCRIPTION_SOFT_LIMIT = 5000;

/**
 * How long a description READS, in visible characters.
 *
 * Both product forms used to show the raw HTML length against the limit above,
 * which was close enough while the only markup was paragraph and list tags. It
 * stops being close enough the moment an image is allowed in: a single Firebase
 * download URL is roughly 300 characters of markup carrying no text at all, so
 * three images and a table would have shown a seller "4,200 / 5,000" for a
 * description of two short paragraphs, and pushed them into cutting prose to
 * fix a number that was never measuring prose.
 */
export function descriptionTextLength(html: string | null | undefined): number {
  if (!html) return 0;
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, ' ')
    .trim()
    .length;
}

/**
 * Matches one complete img tag and captures its src.
 *
 * A regex rather than DOMParser because both product forms are client
 * components that still server-render, and DOMParser does not exist in Node.
 * It is safe here in a way it would not be in general: the only HTML these
 * helpers ever see is what Tiptap's own serializer produced a moment earlier,
 * which always emits double-quoted attributes and never a raw > inside one.
 * Anything hostile is handled by the sanitizer, not by this.
 */
const IMG_TAG_WITH_SRC = /<img\b[^>]*?\bsrc\s*=\s*"([^"]*)"[^>]*>/gi;

/** Every img src in a description, in document order, duplicates included. */
export function descriptionImageSrcs(html: string | null | undefined): string[] {
  if (!html) return [];
  const found: string[] = [];
  for (const match of html.matchAll(IMG_TAG_WITH_SRC)) found.push(match[1]);
  return found;
}

/**
 * How many images a description currently holds.
 *
 * The ceiling it is compared against is DESCRIPTION_IMAGE_MAX_COUNT, which
 * lives in src/lib/sanitize-html.ts rather than here: that is the module which
 * actually enforces it, and this one pulls in lucide-react, which has no
 * business in the server-rendered SEO body that the sanitizer is imported by.
 */
export function descriptionImageCount(html: string | null | undefined): number {
  return descriptionImageSrcs(html).length;
}

/**
 * How many video embeds a description currently carries.
 *
 * The companion to descriptionImageCount above, and it exists for the same
 * reason: both forms refuse the sixth of either before the seller commits to
 * it, so a cap is met as a message rather than as content that vanishes on
 * save. See DESCRIPTION_EMBED_MAX_COUNT in src/lib/sanitize-html.ts for why
 * embeds need a ceiling of their own despite costing us no storage.
 *
 * Counts `<iframe>` tags rather than validating their srcs. This runs against
 * the LIVE editor document, where the only thing that can have produced an
 * iframe is the editor's own embed command (which only accepts what
 * `toEmbedUrl` normalises), and a count that disagreed with the sanitizer's
 * would refuse a seller an embed they were entitled to.
 */
export function descriptionEmbedCount(html: string | null | undefined): number {
  if (!html) return 0;
  return (html.match(/<iframe\b/gi) || []).length;
}

/**
 * The blob: srcs in a description, deduplicated.
 *
 * These are images the seller has PICKED but not yet saved. Description images
 * ride the product's existing upload pipeline rather than uploading on
 * selection, so between picking and saving the editor holds an object URL. That
 * choice buys one upload path, one progress bar, one quota check, and nothing
 * orphaned in Storage when a seller abandons the form; the cost is this
 * in-flight state, which every helper below exists to handle.
 */
export function unsavedDescriptionImageSrcs(html: string | null | undefined): string[] {
  return [...new Set(descriptionImageSrcs(html).filter((src) => src.startsWith('blob:')))];
}

/**
 * Swap blob: srcs for the storage URLs they were uploaded to. Called once, at
 * save, after the upload jobs resolve. Any src with no entry in the map is left
 * exactly as it was, so an already-saved image is never touched.
 */
export function rewriteDescriptionImageSrcs(
  html: string | null | undefined,
  uploaded: ReadonlyMap<string, string>,
): string {
  if (!html) return '';
  if (uploaded.size === 0) return html;
  return html.replace(IMG_TAG_WITH_SRC, (tag, src: string) => {
    const replacement = uploaded.get(src);
    return replacement ? tag.replace(src, replacement) : tag;
  });
}

/**
 * Drop images that have not been uploaded yet.
 *
 * Used on the copy of the form that gets persisted to localStorage. A blob:
 * URL is scoped to the document that created it and resolves to nothing after a
 * reload, so a restored draft holding one shows a broken-image glyph the seller
 * cannot explain or fix. Dropping the tag loses the same image either way and
 * leaves the rest of the draft — the prose, which is the part worth restoring —
 * intact and correct.
 */
export function stripUnsavedDescriptionImages(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(IMG_TAG_WITH_SRC, (tag, src: string) =>
    src.startsWith('blob:') ? '' : tag);
}

/**
 * The storage paths of every image in a description, for the product's
 * `descriptionImagePaths` companion field.
 *
 * Derived from the stored HTML rather than from the upload list, which is what
 * makes the field self-correcting: an image the seller inserted and then
 * deleted before saving contributes nothing, an image carried over from a
 * previous save is still counted, and the two can never disagree with what the
 * description actually references.
 *
 * SCOPED TO THIS PRODUCT'S OWN DESCRIPTION PREFIX, and that is a safety
 * property rather than tidiness. The list this returns is the input to a DELETE:
 * the edit form diffs it against the previous save and removes whatever dropped
 * out. Unscoped, any storage URL a seller could get into an `<img>` would be
 * treated as a description image of theirs to collect — so pasting their own
 * cover's URL into the description and then removing it would delete the cover,
 * and pasting ANOTHER product's image URL would hand them a way to delete an
 * object belonging to a product they do not own. Both are reachable: the
 * sanitizer admits any URL on our storage hosts, which is exactly what a cover
 * and someone else's gallery image are.
 *
 * Non-storage and unparseable srcs yield nothing.
 */
export function descriptionStoragePaths(
  html: string | null | undefined,
  productId: string,
): string[] {
  const paths = new Set<string>();
  if (!productId) return [];
  const prefix = `products/${productId}/description/`;
  for (const src of descriptionImageSrcs(html)) {
    try {
      const path = storageObjectPathFromUrl(new URL(src));
      if (path && path.startsWith(prefix)) paths.add(path);
    } catch {
      // Relative, blob: or malformed — not a stored object, nothing to track.
    }
  }
  return [...paths];
}

/**
 * Longest a FAQ question may be.
 *
 * A question is a one-line trigger on a collapsible row, sharing that row with
 * a chevron: past about this length it stops being a question and becomes the
 * answer, wrapping to three lines before the buyer has opened anything. The
 * ANSWER is where length belongs, and it has its own, far larger allowance.
 *
 * Enforced on both product forms. The product page wraps rather than truncates,
 * so a longer question stored before this cap still renders correctly.
 */
export const FAQ_QUESTION_MAX = 200;

/**
 * Most FAQ entries a product may carry.
 *
 * One number, because the two forms had three: the create form's Add button
 * stopped at 10 while its own guard toasted "Maximum 20 FAQ items allowed", so
 * the message was unreachable and the stated limit was wrong. The edit form
 * said 10 in both places. 10 is the real cap and this is now the only place it
 * is written down.
 */
export const FAQ_MAX_ITEMS = 10;

/**
 * Adapt a drop onto a dropzone into the shape the file-input handlers already
 * take, so a dropped file goes through exactly the same validation, size caps
 * and rejection toasts as a picked one.
 *
 * Reusing the change handler rather than writing a parallel drop path is the
 * point: a second path is a second place for the tier cap, the MIME allowlist
 * and the count limit to be enforced differently, which is how a dropped file
 * ends up accepted where a picked one would have been refused.
 *
 * Returns null when the drop carried no files (dragged text, a link), leaving
 * the caller nothing to do.
 */
export function dropAsFileSelectEvent(
  dataTransfer: DataTransfer | null,
): React.ChangeEvent<HTMLInputElement> | null {
  const files = dataTransfer?.files;
  if (!files || files.length === 0) return null;
  // The handlers read `target.files` and then clear `target.value` so the same
  // filename can be re-picked later. Both work on this stand-in; nothing else
  // about the event is touched.
  return { target: { files, value: '' } } as unknown as React.ChangeEvent<HTMLInputElement>;
}
